import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Sticker, SIGNAL, SIGNAL_WORD } from "../Sticker";
import { Detail } from "./Detail";

const MONO = "JetBrains Mono, monospace";

const LAYERS = [
  { n: "00", name: "Release liner", fill: "#FFFFFF", text: "Peeled off before the label is applied. Protects the adhesive until then." },
  { n: "01", name: "Food-safe adhesive layer", fill: "#E4DFD3", text: "Keeps the sticker in place on the package and is safe for food contact. It sits on the back of the printed layer, never on the food side." },
  { n: "02", name: "Printed layer with viewing window", fill: "#FFFFFF", text: "Carries the design and a cutout that forms the colour display area. The print itself is protective and non-reactive." },
  { n: "03", name: "Chemical indicator layer", fill: "#F7F6F2", text: "Contains a pH-sensitive dye, for example bromocresol purple, that changes colour when it reacts with volatile spoilage compounds. It is what you see through the window." },
  { n: "04", name: "Nano / membrane layer", fill: "#EFEDE6", text: "A semi-permeable membrane on the food side. Small volatile molecules such as ammonia pass through; the dye is blocked and cannot leak into the food." },
];

const DESKTOP = { w: 640, h: 560, cx: 250, rx: 190, ry: 50, gap: 86, top: 100, th: 7, hrx: 62, hry: 16, font: 10, bottom: 548, guide: 530 };
const MOBILE = { w: 360, h: 470, cx: 168, rx: 140, ry: 38, gap: 76, top: 62, th: 6, hrx: 46, hry: 12, font: 11, bottom: 458, guide: 440 };

export const LayersDiagram = () => {
  const [sel, setSel] = useState(3);
  const [state, setState] = useState("green");
  const [narrow, setNarrow] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.1 });
  const expanded = narrow || inView;
  const l = LAYERS[sel];
  const G = narrow ? MOBILE : DESKTOP;

  useEffect(() => {
    setNarrow(window.matchMedia("(max-width: 1279px)").matches);
  }, []);

  return (
    <div className="grid grid-cols-12 gap-x-6 gap-y-8" data-testid="layers-diagram">
      <div className="col-span-12 lg:col-span-7" ref={ref}>
        <svg viewBox={`0 0 ${G.w} ${G.h}`} className="w-full h-auto">
          <text x={G.cx} y="16" textAnchor="middle" fontFamily={MONO} fontSize={G.font} letterSpacing="2" fill="#595959">TOP</text>
          <line x1={G.cx} y1="26" x2={G.cx} y2={G.guide} stroke="#0D1B2A" strokeOpacity="0.18" strokeDasharray="2 5" />
          {LAYERS.map((ly, i) => {
            const on = i === sel;
            return (
              <motion.g
                key={ly.n}
                initial={false}
                animate={{ y: expanded ? G.top + i * G.gap : G.top + i * 10 }}
                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.1 + i * 0.05 }}
                onClick={() => setSel(i)}
                style={{ cursor: "pointer" }}
                opacity={on ? 1 : 0.55}
                data-testid={`layer-${ly.n}`}
                role="button"
                aria-pressed={on}
              >
                <rect x={G.cx - G.rx} y={-G.ry} width={G.w - (G.cx - G.rx) - 2} height={G.ry * 2 + G.th} fill="transparent" />
                <ellipse cx={G.cx} cy={G.th} rx={G.rx} ry={G.ry} fill={ly.fill} stroke="#0D1B2A" strokeWidth={on ? 1.5 : 1} />
                <ellipse cx={G.cx} cy={0} rx={G.rx} ry={G.ry} fill={ly.fill} stroke="#0D1B2A" strokeWidth={on ? 1.5 : 1} />
                {i === 2 && <ellipse cx={G.cx} cy={0} rx={G.hrx} ry={G.hry} fill="#F2F3F5" stroke="#0D1B2A" />}
                {i === 3 && <motion.ellipse cx={G.cx} cy={0} rx={G.hrx} ry={G.hry} animate={{ fill: SIGNAL[state] }} transition={{ duration: 0.9 }} data-testid="layer-indicator-dot" />}
                <line x1={G.cx + G.rx + 8} y1={0} x2={G.w - 6} y2={0} stroke="#0D1B2A" strokeOpacity={on ? 0.8 : 0.3} />
                <text x={G.w - 6} y={-6} textAnchor="end" fontFamily={MONO} fontSize={G.font} letterSpacing="1.5" fill="#0D1B2A">{ly.n}</text>
              </motion.g>
            );
          })}
          <text x={G.cx} y={G.bottom} textAnchor="middle" fontFamily={MONO} fontSize={G.font} letterSpacing="2" fill="#595959">BOTTOM / FOOD SIDE</text>
        </svg>
      </div>

      <div className="col-span-12 lg:col-span-5 flex flex-col gap-8">
        <Detail n={l.n} title={l.name} text={l.text} testId="layers-detail" />
        <div>
          <div className="w-[46vw] max-w-[180px]">
            <Sticker state={state} testId="layers-sticker" />
          </div>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="layers-state-switch">
            {["green", "yellow", "red"].map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setState(k)}
                aria-pressed={state === k}
                className="flex items-center gap-2 border px-3 py-3 min-h-[44px] eyebrow transition-colors duration-300"
                style={{ borderColor: state === k ? "#0D1B2A" : "rgba(13,27,42,0.25)", background: state === k ? "#FFFFFF" : "transparent" }}
                data-testid={`layers-state-${k}`}
              >
                <span className="block h-2 w-2" style={{ background: SIGNAL[k] }} />
                {SIGNAL_WORD[k]}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
